import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  DesignTokens,
  PartialDesignTokens,
  CSSTokenMap,
} from './design-tokens.interface';
import { DEFAULT_DESIGN_TOKENS } from './design-tokens.defaults';

const THEME_CACHE_KEY = 'plp_theme_tokens';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private activeTokens: DesignTokens = DEFAULT_DESIGN_TOKENS;
  private tokensSubject = new BehaviorSubject<DesignTokens>(DEFAULT_DESIGN_TOKENS);
  private isCustomTheme = false;

  tokens$: Observable<DesignTokens> = this.tokensSubject.asObservable();

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  // ── Public API ─────────────────────────────────────────────

  /**
   * Apply a partial set of tenant tokens.
   * Merges with defaults so only overrides need to be provided.
   */
  applyTheme(overrides: PartialDesignTokens): void {
    this.activeTokens = this.deepMerge(DEFAULT_DESIGN_TOKENS, overrides);
    this.tokensSubject.next(this.activeTokens);
    this.isCustomTheme = true;

    if (isPlatformBrowser(this.platformId)) {
      const cssMap = this.tokensToCSSMap(this.activeTokens);
      this.applyCSSProperties(cssMap);
      this.cacheTheme(overrides);
    }
  }

  /** Reset to default tokens (remove tenant theming). */
  resetToDefaults(): void {
    this.activeTokens = DEFAULT_DESIGN_TOKENS;
    this.tokensSubject.next(this.activeTokens);
    this.isCustomTheme = false;

    if (isPlatformBrowser(this.platformId)) {
      const cssMap = this.tokensToCSSMap(DEFAULT_DESIGN_TOKENS);
      this.applyCSSProperties(cssMap);
      this.clearCache();
    }
  }

  /**
   * Load theme from localStorage cache.
   * Call from APP_INITIALIZER to persist theme across reloads.
   * Returns true if a cached theme was applied.
   */
  loadCachedTheme(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;

    try {
      const cached = localStorage.getItem(THEME_CACHE_KEY);
      if (cached) {
        const overrides: PartialDesignTokens = JSON.parse(cached);
        this.applyTheme(overrides);
        return true;
      }
    } catch (e) {
      console.warn('ThemeService: Failed to load cached theme', e);
    }
    return false;
  }

  /**
   * Get the resolved value of a CSS custom property.
   * Useful for code that needs runtime colors (e.g., Chart.js).
   */
  getTokenValue(cssPropertyName: string): string | undefined {
    if (isPlatformBrowser(this.platformId)) {
      return getComputedStyle(document.documentElement)
        .getPropertyValue(cssPropertyName)
        .trim() || undefined;
    }
    const cssMap = this.tokensToCSSMap(this.activeTokens);
    return cssMap[cssPropertyName];
  }

  /** Snapshot of the currently active tokens. */
  getActiveTokens(): Readonly<DesignTokens> {
    return this.activeTokens;
  }

  /** Whether a custom (non-default) theme is active. */
  hasCustomTheme(): boolean {
    return this.isCustomTheme;
  }

  // ── Private ────────────────────────────────────────────────

  // NOTE: A critical subset of these mappings is duplicated in src/index.html
  // for anti-FOUC pre-bootstrap injection. Keep them in sync.
  private tokensToCSSMap(tokens: DesignTokens): CSSTokenMap {
    const map: CSSTokenMap = {};

    // Color
    const c = tokens.color;
    map['--color-primary'] = c.primary;
    map['--color-primary-shade'] = c.primaryShade;
    map['--color-primary-shade-dark'] = c.primaryShadeDark;
    map['--color-primary-tint'] = c.primaryTint;
    map['--color-primary-tint-light'] = c.primaryTintLight;
    map['--color-primary-tint-lighter'] = c.primaryTintLighter;
    map['--color-primary-tint-muted'] = c.primaryTintMuted;
    map['--color-primary-pale'] = c.primaryPale;
    map['--color-secondary'] = c.secondary;
    map['--color-secondary-shade'] = c.secondaryShade;
    map['--color-secondary-tint'] = c.secondaryTint;
    map['--color-secondary-tint-24'] = c.secondaryTint24;
    map['--color-tertiary'] = c.tertiary;
    map['--color-tertiary-shade'] = c.tertiaryShade;
    map['--color-tertiary-tint'] = c.tertiaryTint;
    map['--color-accent'] = c.accent;
    map['--color-accent-light'] = c.accentLight;
    map['--color-error'] = c.error;
    map['--color-error-shade'] = c.errorShade;
    map['--color-error-tint'] = c.errorTint;
    map['--color-error-schemes'] = c.errorSchemes;
    map['--color-success'] = c.success;
    map['--color-success-shade'] = c.successShade;
    map['--color-success-tint'] = c.successTint;
    map['--color-warning'] = c.warning;
    map['--color-warning-shade'] = c.warningShade;
    map['--color-warning-tint'] = c.warningTint;
    map['--color-warning-dark'] = c.warningDark;
    map['--color-inactive'] = c.inactive;
    map['--color-inactive-shade'] = c.inactiveShade;
    map['--color-inactive-tint'] = c.inactiveTint;
    map['--color-text-primary'] = c.textPrimary;
    map['--color-text-secondary'] = c.textSecondary;
    map['--color-text-dark'] = c.textDark;
    map['--color-background-page'] = c.backgroundPage;
    map['--color-background-gray'] = c.backgroundGray;
    map['--color-background-gray-shade'] = c.backgroundGrayShade;
    map['--color-background-gray-shade-24'] = c.backgroundGrayShade24;
    map['--color-background-gray-tint'] = c.backgroundGrayTint;
    map['--color-background-card'] = c.backgroundCard;
    map['--color-background-input'] = c.backgroundInput;
    map['--color-background-neutral'] = c.backgroundNeutral;
    map['--color-stroke-default'] = c.strokeDefault;
    map['--color-stroke-table'] = c.strokeTable;
    map['--color-white'] = c.white;
    map['--color-shadow-gray'] = c.shadowGray;
    map['--color-shadow-blue'] = c.shadowBlue;

    // State colors
    const sc = tokens.stateColors;
    map['--color-state-success-bg'] = sc.successBg;
    map['--color-state-success-text'] = sc.successText;
    map['--color-state-info-bg'] = sc.infoBg;
    map['--color-state-info-text'] = sc.infoText;
    map['--color-state-warning-bg'] = sc.warningBg;
    map['--color-state-warning-text'] = sc.warningText;
    map['--color-state-error-bg'] = sc.errorBg;
    map['--color-state-neutral-bg'] = sc.neutralBg;

    // Chart colors
    const ch = tokens.chartColors;
    map['--color-chart-purple'] = ch.purple;
    map['--color-chart-lime-green'] = ch.limeGreen;
    map['--color-chart-ice-blue'] = ch.iceBlue;
    map['--color-chart-raspberry'] = ch.raspberry;
    map['--color-chart-forest-green'] = ch.forestGreen;
    map['--color-chart-medium-green'] = ch.mediumGreen;
    map['--color-chart-solar-yellow'] = ch.solarYellow;

    // Typography
    const t = tokens.typography;
    map['--font-family-primary'] = t.fontFamilyPrimary;
    map['--font-family-secondary'] = t.fontFamilySecondary;
    map['--font-size-xs'] = t.fontSizeXs;
    map['--font-size-sm'] = t.fontSizeSm;
    map['--font-size-md'] = t.fontSizeMd;
    map['--font-size-base'] = t.fontSizeBase;
    map['--font-size-lg'] = t.fontSizeLg;
    map['--font-size-xl'] = t.fontSizeXl;
    map['--font-size-2xl'] = t.fontSize2xl;
    map['--font-size-3xl'] = t.fontSize3xl;
    map['--font-weight-regular'] = t.fontWeightRegular;
    map['--font-weight-medium'] = t.fontWeightMedium;
    map['--font-weight-semibold'] = t.fontWeightSemibold;
    map['--font-weight-bold'] = t.fontWeightBold;
    map['--line-height-tight'] = t.lineHeightTight;
    map['--line-height-normal'] = t.lineHeightNormal;
    map['--line-height-relaxed'] = t.lineHeightRelaxed;

    // Spacing
    const sp = tokens.spacing;
    map['--spacing-xxs'] = sp.xxs;
    map['--spacing-xs'] = sp.xs;
    map['--spacing-sm'] = sp.sm;
    map['--spacing-md'] = sp.md;
    map['--spacing-lg'] = sp.lg;
    map['--spacing-xl'] = sp.xl;
    map['--spacing-2xl'] = sp['2xl'];
    map['--spacing-3xl'] = sp['3xl'];
    map['--spacing-4xl'] = sp['4xl'];

    // Borders
    const b = tokens.borders;
    map['--border-radius-sm'] = b.radiusSm;
    map['--border-radius-md'] = b.radiusMd;
    map['--border-radius-lg'] = b.radiusLg;
    map['--border-radius-xl'] = b.radiusXl;
    map['--border-radius-pill'] = b.radiusPill;
    map['--border-radius-full'] = b.radiusFull;
    map['--border-width-thin'] = b.widthThin;
    map['--border-width-medium'] = b.widthMedium;

    // Shadows
    const sh = tokens.shadows;
    map['--shadow-none'] = sh.none;
    map['--shadow-sm'] = sh.sm;
    map['--shadow-md'] = sh.md;
    map['--shadow-card'] = sh.card;
    map['--shadow-elevated'] = sh.elevated;

    // Transitions
    const tr = tokens.transitions;
    map['--transition-duration-fast'] = tr.durationFast;
    map['--transition-duration-normal'] = tr.durationNormal;
    map['--transition-duration-slow'] = tr.durationSlow;
    map['--transition-easing-default'] = tr.easingDefault;
    map['--transition-easing-in-out'] = tr.easingInOut;

    // Gradients
    const g = tokens.gradients;
    map['--gradient-primary'] = g.primary;
    map['--gradient-border'] = g.border;

    return map;
  }

  private applyCSSProperties(map: CSSTokenMap): void {
    const root = document.documentElement;
    for (const [prop, value] of Object.entries(map)) {
      root.style.setProperty(prop, value);
    }
  }

  private deepMerge(base: DesignTokens, overrides: PartialDesignTokens): DesignTokens {
    const result = { ...base };

    for (const key of Object.keys(overrides) as Array<keyof DesignTokens>) {
      const overrideGroup = overrides[key];
      if (overrideGroup && typeof overrideGroup === 'object') {
        result[key] = { ...base[key], ...overrideGroup } as any;
      }
    }

    return result;
  }

  private cacheTheme(overrides: PartialDesignTokens): void {
    try {
      localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(overrides));
    } catch (e) {
      console.warn('ThemeService: Failed to cache theme', e);
    }
  }

  private clearCache(): void {
    try {
      localStorage.removeItem(THEME_CACHE_KEY);
    } catch {
      // ignore
    }
  }
}
